import { useRef, useState } from 'react';

import { useFileTree } from '@/api-models/sandbox-files';
import { Spinner } from '@/ui/Spinner';

import { DEFAULT_TREE_WIDTH, MAX_TREE_WIDTH, MIN_TREE_WIDTH } from '../panel-layout';
import { ResizeHandle } from '../ResizeHandle';

import { FileTabPane } from './files/FileTabPane';
import { FileTabStrip } from './files/FileTabStrip';
import { FileTree } from './files/FileTree';

export function FilesTab({ base }: { base: string }) {
  const { tree, workspaceRoot, isLoading: treeLoading } = useFileTree(base);
  const [openPaths, setOpenPaths] = useState<string[]>([]);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [previewPaths, setPreviewPaths] = useState<Set<string>>(new Set());
  // No collapse here — the tab means nothing without a tree.
  const [treeWidth, setTreeWidth] = useState(DEFAULT_TREE_WIDTH);
  const treeRef = useRef<HTMLDivElement>(null);

  const openFile = (path: string) => {
    setOpenPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
    setActivePath(path);
  };

  const openPreview = (path: string) => {
    setOpenPaths((prev) => (prev.includes(path) ? prev : [...prev, path]));
    setPreviewPaths((prev) => new Set(prev).add(path));
    setActivePath(path);
  };

  const setPreviewMode = (path: string, isPreview: boolean) => {
    setPreviewPaths((prev) => {
      if (isPreview === prev.has(path)) return prev;
      const next = new Set(prev);
      if (isPreview) next.add(path);
      else next.delete(path);
      return next;
    });
  };

  const closeFile = (path: string) => {
    setOpenPaths((prev) => prev.filter((p) => p !== path));
    setPreviewPaths((prev) => {
      if (!prev.has(path)) return prev;
      const next = new Set(prev);
      next.delete(path);
      return next;
    });
    if (activePath !== path) return;
    const index = openPaths.indexOf(path);
    setActivePath(openPaths[index + 1] ?? openPaths[index - 1] ?? null);
  };

  const closeOthers = (path: string) => {
    setOpenPaths([path]);
    setPreviewPaths((prev) => (prev.has(path) ? new Set([path]) : new Set()));
    setActivePath(path);
  };

  const closeToRight = (path: string) => {
    const kept = openPaths.slice(0, openPaths.indexOf(path) + 1);
    setOpenPaths(kept);
    setPreviewPaths((prev) => new Set([...prev].filter((p) => kept.includes(p))));
    if (activePath && openPaths.indexOf(activePath) > openPaths.indexOf(path)) {
      setActivePath(path);
    }
  };

  const closeAll = () => {
    setOpenPaths([]);
    setPreviewPaths(new Set());
    setActivePath(null);
  };

  return (
    <div className="flex min-h-0 flex-1">
      <div ref={treeRef} style={{ width: treeWidth, maxWidth: '70%' }} className="flex shrink-0 flex-col bg-surface-1">
        <div className="flex h-8.5 shrink-0 items-center border-b border-line-subtle px-3 text-2xs font-semibold tracking-wide text-fg-tertiary">
          EXPLORER
        </div>
        {treeLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner size={5} />
          </div>
        ) : (
          <FileTree base={base} nodes={tree} selectedPath={activePath} onSelect={openFile} onPreview={openPreview} />
        )}
      </div>
      <ResizeHandle
        panelRef={treeRef}
        width={treeWidth}
        onWidth={setTreeWidth}
        min={MIN_TREE_WIDTH}
        max={MAX_TREE_WIDTH}
        edge="end"
        onReset={() => setTreeWidth(DEFAULT_TREE_WIDTH)}
        label="Resize file explorer"
      />
      <div className="flex min-w-0 flex-1 flex-col bg-surface-1">
        {openPaths.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm text-fg-tertiary">
            Select a file to view its contents.
          </div>
        ) : (
          <>
            <FileTabStrip
              workspaceRoot={workspaceRoot}
              paths={openPaths}
              activePath={activePath}
              onSelect={setActivePath}
              onClose={closeFile}
              onCloseOthers={closeOthers}
              onCloseToRight={closeToRight}
              onCloseAll={closeAll}
              onPreview={openPreview}
            />
            <div className="relative min-h-0 flex-1">
              {openPaths.map((path) => (
                <FileTabPane
                  key={path}
                  base={base}
                  path={path}
                  active={activePath === path}
                  isPreview={previewPaths.has(path)}
                  onPreviewChange={(isPreview) => setPreviewMode(path, isPreview)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
